import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import {
  FileDownloadOutlined as ExportIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../../components";
import { ReceiptDetail } from "../../../components/reports/ReceiptDetail";
import { baseAxios, Context } from "../../../utils/config";
import { PAYMENT_METHOD_LABELS } from "../../../utils/paymentMethods";
import { colors } from "../..";

const MAX_RANGE_DAYS = 366;

const SUMMARY_CARDS = [
  { key: "totalRevenue", label: "Total Revenue", money: true },
  { key: "totalTips", label: "Total Tips", money: true },
  { key: "totalTransactions", label: "Total Transactions", money: false },
  { key: "dateRange", label: "Report Period", money: false },
];

function formatDateToISO(date) {
  if (!date || !dayjs(date).isValid()) return "";
  return dayjs(date).format("YYYY-MM-DD");
}

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return "$0.00";
  return `$${n.toFixed(2)}`;
}

function validateDateRange(startDate, endDate) {
  if (
    !startDate ||
    !endDate ||
    !dayjs(startDate).isValid() ||
    !dayjs(endDate).isValid()
  ) {
    toast.error("Please select both start and end dates", { duration: 3000 });
    return false;
  }
  if (dayjs(startDate).isAfter(dayjs(endDate), "day")) {
    toast.error("Start date must be before or equal to end date", {
      duration: 3000,
    });
    return false;
  }
  const daysDiff = dayjs(endDate).diff(dayjs(startDate), "day");
  if (daysDiff > MAX_RANGE_DAYS) {
    toast.error(`Date range cannot exceed ${MAX_RANGE_DAYS} days`, {
      duration: 3000,
    });
    return false;
  }
  return true;
}

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (typeof va === "number" || typeof vb === "number") {
    return (Number(va) || 0) - (Number(vb) || 0);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

function mapApiError(error) {
  if (error.response?.status === 403) {
    return "Access denied. You don't have permission to view this report.";
  }
  if (error.response?.status === 400) {
    return error.response.data?.message || "Invalid request parameters";
  }
  if (error.response?.status === 404) {
    return "Checkout summary endpoint not found";
  }
  if (error.response?.status === 500) {
    return "Server error. Please try again later.";
  }
  if (error.message === "Network Error") {
    return "Network error. Please check your connection and try again.";
  }
  return (
    error.response?.data?.message ||
    "Failed to load checkout summary. Please try again."
  );
}

const CheckoutSummaryReport = () => {
  const { location: globalLocation } = useContext(Context);

  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [sortBy, setSortBy] = useState("totalRevenue");
  const [sortOrder, setSortOrder] = useState("desc");

  const loadReport = useCallback(async () => {
    if (!validateDateRange(startDate, endDate)) {
      return;
    }

    setLoading(true);
    setHasRun(true);
    try {
      const response = await baseAxios.get("/sales-reports", {
        params: {
          groupBy: "paymentMethod",
          startDate: formatDateToISO(startDate),
          endDate: formatDateToISO(endDate),
        },
      });

      if (!response.data) {
        toast.error("No data returned from server", { duration: 3000 });
        setRows([]);
        setSummary(null);
        return;
      }

      const { groupBy: responseGroupBy, summary: nextSummary, data } =
        response.data;

      if (responseGroupBy && responseGroupBy !== "paymentMethod") {
        console.warn(
          `Expected groupBy=paymentMethod, got groupBy=${responseGroupBy}`
        );
      }

      setSummary(nextSummary || null);

      if (!Array.isArray(data)) {
        setRows([]);
        return;
      }

      const totalRevenue = Number(nextSummary?.totalRevenue || 0);
      const transformed = data.map((group, index) => {
        const methodKey = group.paymentMethod ?? "unspecified";
        const revenue = Number(group.totalRevenue || 0);
        const percentage =
          totalRevenue > 0
            ? Number(((revenue / totalRevenue) * 100).toFixed(1))
            : 0;

        return {
          ...group,
          groupKey: `payment-${methodKey}-${index}`,
          paymentMethodLabel:
            PAYMENT_METHOD_LABELS[group.paymentMethod] || "Unspecified",
          totalRevenue: revenue,
          totalTips: Number(group.totalTips || 0),
          transactionCount: Number(group.transactionCount || 0),
          percentage,
        };
      });

      setRows(transformed);
    } catch (error) {
      setRows([]);
      setSummary(null);
      toast.error(mapApiError(error), { duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [endDate, startDate, globalLocation?.location_id]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [rows, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const handleExport = useCallback(async () => {
    if (!sortedRows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }

    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Checkout Summary");
      worksheet.columns = [
        { header: "Payment Method", key: "paymentMethodLabel", width: 18 },
        { header: "Total Revenue", key: "totalRevenue", width: 14 },
        { header: "Total Tips", key: "totalTips", width: 12 },
        { header: "Transaction Count", key: "transactionCount", width: 18 },
        { header: "% of Total", key: "percentage", width: 12 },
      ];
      worksheet.getRow(1).font = { bold: true };
      sortedRows.forEach((row) => {
        worksheet.addRow({
          paymentMethodLabel: row.paymentMethodLabel,
          totalRevenue: row.totalRevenue,
          totalTips: row.totalTips,
          transactionCount: row.transactionCount,
          percentage: row.percentage,
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const dateStr = dayjs().format("YYYY-MM-DD");
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `CheckoutSummary_${dateStr}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [sortedRows]);

  const columns = useMemo(
    () => [
      {
        id: "paymentMethodLabel",
        label: "Payment Method",
        field: "paymentMethodLabel",
        minWidth: 180,
      },
      {
        id: "totalRevenue",
        label: "Total Revenue",
        field: "totalRevenue",
        minWidth: 140,
        align: "right",
        render: (value) => formatMoney(value),
      },
      {
        id: "totalTips",
        label: "Total Tips",
        field: "totalTips",
        minWidth: 120,
        align: "right",
        render: (value) => formatMoney(value),
      },
      {
        id: "transactionCount",
        label: "Transactions",
        field: "transactionCount",
        minWidth: 130,
        align: "center",
        render: (value) => Number(value) || 0,
      },
      {
        id: "percentage",
        label: "% of Total",
        field: "percentage",
        minWidth: 110,
        align: "center",
        render: (value) => `${Number(value) || 0}%`,
      },
    ],
    []
  );

  const renderDetail = useCallback(
    (row) => <ReceiptDetail data={row} showPaymentMethod />,
    []
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ px: 8, py: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mt: 1.5,
            mb: 2,
          }}
        >
          <Box>
            <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
              Checkout Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Revenue breakdown by payment method, with expandable receipt
              details.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <DatePicker
              label="Start date"
              value={startDate}
              onChange={(value) => setStartDate(value)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    minWidth: 160,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "background.paper",
                      borderRadius: "6px",
                    },
                  },
                },
              }}
            />
            <DatePicker
              label="End date"
              value={endDate}
              onChange={(value) => setEndDate(value)}
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    minWidth: 160,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "background.paper",
                      borderRadius: "6px",
                    },
                  },
                },
              }}
            />
            <IconButton
              aria-label="Refresh report"
              onClick={loadReport}
              disabled={loading}
              size="medium"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                borderRadius: "6px",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              <RefreshIcon />
            </IconButton>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              disabled={loading || !sortedRows.length}
              sx={{
                minHeight: 40,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={loadReport}
              disabled={loading}
              sx={{
                minHeight: 40,
                px: 2,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {loading ? "Loading…" : "Run Report"}
            </Button>
          </Box>
        </Box>

        {summary && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(4, 1fr)",
              },
              gap: 1.5,
              mb: 2.5,
            }}
          >
            {SUMMARY_CARDS.map(({ key, label, money }) => {
              let valueNode;
              if (key === "dateRange") {
                valueNode = (
                  <>
                    <Typography fontWeight={700} sx={{ fontSize: "1rem", lineHeight: 1.3 }}>
                      {summary.dateRange?.startDate} to{" "}
                      {summary.dateRange?.endDate}
                    </Typography>
                    {summary.locationFilter ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {summary.locationFilter}
                      </Typography>
                    ) : null}
                  </>
                );
              } else if (money) {
                valueNode = (
                  <Typography fontWeight={700} sx={{ fontSize: "1.75rem", lineHeight: 1.1 }}>
                    {formatMoney(summary[key])}
                  </Typography>
                );
              } else {
                valueNode = (
                  <Typography fontWeight={700} sx={{ fontSize: "1.75rem", lineHeight: 1.1 }}>
                    {Number(summary[key]) || 0}
                  </Typography>
                );
              }

              return (
                <Box
                  key={key}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "6px",
                    bgcolor: "background.paper",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 0.75,
                      bgcolor: "secondary.main",
                      borderBottom: "1px solid",
                      borderColor: "primary.main",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.gold,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2, py: 1.5 }}>{valueNode}</Box>
                </Box>
              );
            })}
          </Box>
        )}

        <PaginatedTable
          columns={columns}
          rows={sortedRows}
          totalRowCount={sortedRows.length}
          loading={loading}
          onPaginationChange={noopPagination}
          onSortChange={handleSortChange}
          initialOrderBy="totalRevenue"
          initialOrder="desc"
          searchQuery={`${formatDateToISO(startDate)}|${formatDateToISO(endDate)}|${globalLocation?.location_id ?? "all"}`}
          clientSidePagination
          skeletonHeight="40px"
          getRowKey={(row) => row.groupKey}
          renderDetail={renderDetail}
          emptyMessage={
            hasRun
              ? "No checkout data for this date range"
              : "Click Run Report to load checkout data"
          }
        />
      </Box>
    </LocalizationProvider>
  );
};

export default CheckoutSummaryReport;
